import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Building2, User } from "lucide-react";
import { PageHeader } from "@/components/layout/Layout";
import {
  Badge,
  Button,
  Input,
  SortTh,
  useSort,
  LoadError,
} from "@/components/ui";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";

export function Customers() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const utils = trpc.useUtils();
  const addToast = useAppStore((s) => s.addToast);
  // "Neuer Kunde": kein Pop-up, sondern direkt die normale Kundenseite. Dafür wird der Kunde mit
  // Platzhalternamen angelegt (Kundennummer wird dabei vergeben) und gleich zum Ausfüllen geöffnet.
  const create = trpc.customer.createDraft.useMutation({
    onSuccess: (c) => {
      utils.customer.list.invalidate();
      navigate(`/kunden/${c.id}`);
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const list = trpc.customer.list.useQuery({ search: search || undefined });
  const { sorted, sort, toggle } = useSort(list.data ?? [], {
    name: (c) => c.name,
    number: (c) => c.customerNumber,
    email: (c) => c.email ?? c.phone,
    phone: (c) => c.phone,
    activity: (c) => c._count.addresses + c._count.journal,
  });

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Kunden"
        actions={
          <Button
            variant="primary"
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            <Plus className="w-4 h-4" />
            Neuer Kunde
          </Button>
        }
      />
      <div className="px-6 py-3 border-b border-border-subtle">
        <div className="relative max-w-sm">
          <Search className="w-4 h-4 text-text-tertiary absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            className="pl-8"
            placeholder="Name, Kundennr., Adresse, Ort, Telefon …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-auto">
        {list.isLoading ? (
          <p className="p-6 text-sm text-text-tertiary">Lädt …</p>
        ) : list.isError ? (
          <LoadError onRetry={() => list.refetch()} />
        ) : !list.data?.length ? (
          <div className="p-10 text-center text-sm text-text-tertiary">
            {search
              ? "Keine Treffer."
              : "Noch keine Kunden. Lege den ersten an."}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <SortTh
                  label="Name"
                  sortKey="name"
                  sort={sort}
                  onSort={toggle}
                />
                <SortTh
                  label="Kd-Nr."
                  sortKey="number"
                  sort={sort}
                  onSort={toggle}
                />
                <SortTh
                  label="E-Mail"
                  sortKey="email"
                  sort={sort}
                  onSort={toggle}
                />
                <SortTh
                  label="Telefon"
                  sortKey="phone"
                  sort={sort}
                  onSort={toggle}
                />
                <SortTh
                  label="Adressen · Verlauf"
                  sortKey="activity"
                  sort={sort}
                  onSort={toggle}
                  className="text-right"
                />
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => navigate(`/kunden/${c.id}`)}
                  className="cursor-pointer"
                >
                  <td>
                    <div className="flex items-center gap-2">
                      {c.kind === "company" ? (
                        <Building2 className="w-4 h-4 text-text-tertiary" />
                      ) : (
                        <User className="w-4 h-4 text-text-tertiary" />
                      )}
                      <span className="font-medium text-text-primary">
                        {c.name}
                      </span>
                      {!c.isActive && <Badge tone="warning">inaktiv</Badge>}
                    </div>
                  </td>
                  <td className="text-text-secondary">
                    {c.customerNumber ?? "—"}
                  </td>
                  <td className="text-text-secondary">
                    {c.email ?? c.phone ?? "—"}
                  </td>
                  <td className="text-text-secondary">{c.phone ?? "—"}</td>
                  <td className="text-right text-text-tertiary text-xs">
                    {c._count.addresses} · {c._count.journal}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
